/**
 * Hook principal para gestionar una tarea cron individual
 * Combina polling, estado, countdown y ejecución
 */
import { useState, useEffect, useCallback } from 'react'
import { CronTasksService } from '../../services/cron/cronTasksService'
import { useCronTaskPolling } from './useCronTaskPolling'
import { useCronTaskCountdown } from './useCronTaskCountdown'
import { useCronTaskExecution } from './useCronTaskExecution'
import type { CronTaskConfig } from '../../config/cron/cronTasksConfig'
import type { CronTaskStatusData } from '../../types/cron/cronTasks'

interface UseCronTaskReturn {
  status: CronTaskStatusData | null
  count: number | null
  isLoading: boolean
  error: string | null
  countdown: {
    hours: number
    minutes: number
    seconds: number
    humanReadable: string
    isExpired: boolean
  }
  execute: (params?: { limit?: number; batchSize?: number }) => Promise<void>
  refetch: () => Promise<void>
}

/**
 * Hook que gestiona el ciclo de vida completo de una tarea cron:
 * - Obtiene y actualiza el estado periódicamente
 * - Obtiene el contador de pendientes (si aplica)
 * - Calcula el countdown hasta la próxima ejecución
 * - Permite ejecutar manualmente
 */
export function useCronTask(
  config: CronTaskConfig
): UseCronTaskReturn {
  const [status, setStatus] = useState<CronTaskStatusData | null>(null)
  const [count, setCount] = useState<number | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // Función para obtener el estado
  const fetchStatus = useCallback(async () => {
    try {
      setIsLoading(true)
      const response = await CronTasksService.getTaskStatus(
        config.id,
        config.endpoints.status
      )
      setStatus(response.task)
      
      // Para webhook-retry-failed, extraer el count del status si está disponible
      if (config.id === 'webhook-retry-failed' && (response as any).webhookCount !== undefined) {
        setCount((response as any).webhookCount)
      }
      
      setError(null)
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : 'Error desconocido'
      setError(errorMessage)
      console.error(`Error obteniendo estado de tarea ${config.id}:`, err)
    } finally {
      setIsLoading(false)
    }
  }, [config.id, config.endpoints.status])

  // Función para obtener el contador (si aplica)
  const fetchCount = useCallback(async () => {
    if (!config.endpoints.count) {
      return
    }

    try {
      const response = await CronTasksService.getTaskCount(
        config.id,
        config.endpoints.count
      )
      setCount(response.data?.total ?? null)
    } catch (err) {
      // Silenciar errores de count (no todas las tareas tienen este endpoint)
      console.warn(`Error obteniendo count de tarea ${config.id}:`, err)
    }
  }, [config.id, config.endpoints.count])

  // Polling inteligente del estado
  useCronTaskPolling({
    enabled: true,
    interval: config.pollingInterval || 30000,
    onPoll: fetchStatus,
  })

  // Polling del contador (menos frecuente)
  useCronTaskPolling({
    enabled: !!config.endpoints.count && (config.showCount ?? false),
    interval: 60000, // 1 minuto para el contador
    onPoll: fetchCount,
  })

  // Countdown hasta la próxima ejecución
  const countdown = useCronTaskCountdown({
    nextExecutionISO: status?.nextExecutionISO || null,
    enabled: !!status?.enabled,
  })

  // Ejecución manual
  const { execute: executeTask } = useCronTaskExecution({
    taskId: config.id,
    endpoint: config.endpoints.execute,
    executeMethod: config.executeMethod,
    onSuccess: () => {
      // Refrescar estado y count después de ejecutar
      fetchStatus()
      if (config.endpoints.count) {
        fetchCount()
      }
    },
  })

  // Cargar estado inicial
  useEffect(() => {
    fetchStatus()
    if (config.endpoints.count && config.showCount) {
      fetchCount()
    }
  }, []) // Solo al montar

  return {
    status,
    count,
    isLoading,
    error,
    countdown,
    execute: executeTask,
    refetch: fetchStatus,
  }
}

